/**
 * @license
 * SPDX-License-Identifier: Apache-2.0
 */

import React from 'react';
import { User } from 'lucide-react';
import { getAvatarById, isCustomWCAvatar } from '../avatars';

interface AvatarProps {
  photoURL?: string | null;
  email?: string | null;
  size?: 'sm' | 'md' | 'lg' | 'xl';
  className?: string;
  onClick?: () => void;
}

export const Avatar = ({ photoURL, email, size = 'md', className = '', onClick }: AvatarProps) => {
  const isPreset = isCustomWCAvatar(photoURL);
  const preset = isPreset ? getAvatarById(photoURL!) : undefined;

  let sizeClasses = 'w-8 h-8 text-xs';
  let iconSize = 'text-base';
  if (size === 'sm') {
    sizeClasses = 'w-6 h-6 text-[10px]';
    iconSize = 'text-xs';
  } else if (size === 'lg') {
    sizeClasses = 'w-12 h-12 text-lg';
    iconSize = 'text-xl';
  } else if (size === 'xl') {
    sizeClasses = 'w-20 h-20 text-3xl font-bold';
    iconSize = 'text-4xl';
  }

  const cursorClass = onClick ? 'cursor-pointer' : '';

  // 1. Preset World Cup customized avatar
  if (isPreset && preset) {
    return (
      <div 
        onClick={onClick}
        className={`${sizeClasses} rounded-full flex items-center justify-center font-bold relative border border-white/20 shadow-md overflow-hidden ${preset.bgClass} ${cursorClass} ${className}`}
        title={preset.name}
      >
        <span className={`${iconSize} select-none filter drop-shadow`}>{preset.icon}</span>
      </div>
    );
  }

  // 2. Standard image photoURL (like Google OAuth photo)
  if (photoURL && typeof photoURL === 'string' && photoURL.startsWith('http')) {
    return (
      <img
        src={photoURL}
        alt="User photo"
        onClick={onClick}
        className={`${sizeClasses} rounded-full object-cover border border-blue-500/30 ${cursorClass} ${className}`}
        referrerPolicy="no-referrer"
      />
    );
  }

  // 3. Fallback to initial letter
  const initial = email ? email.substring(0, 1).toUpperCase() : '?';

  return (
    <div
      onClick={onClick}
      className={`${sizeClasses} rounded-full bg-blue-500/15 text-blue-400 border border-blue-500/30 flex items-center justify-center font-mono font-bold ${cursorClass} ${className}`}
    >
      {initial === '?' ? <User size={size === 'sm' ? 12 : size === 'lg' ? 18 : 14} /> : initial}
    </div>
  );
};
